export interface SearchableUser {
  id: string;
  email: string;
  status: string;
}

export const parseSearchTerms = (query: string): string[] =>
  query
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((term) => term.toLowerCase());

export const scoreEmailAgainstTerms = (email: string, terms: string[]): number => {
  const normalizedEmail = email.toLowerCase();

  return terms.filter((term) => normalizedEmail.includes(term)).length;
};

/**
 * Ranks users by how many whitespace-separated query terms appear as substrings in email.
 * Returns users with the highest score, sorted alphabetically by email.
 * An empty or whitespace-only query returns all users unchanged.
 */
export const searchUsersByEmail = <T extends SearchableUser>(
  users: T[],
  query: string,
): T[] => {
  const terms = parseSearchTerms(query);

  if (terms.length === 0) {
    return users;
  }

  const scoredUsers = users.map((user) => ({
    user,
    score: scoreEmailAgainstTerms(user.email, terms),
  }));

  const maxScore = Math.max(...scoredUsers.map(({ score }) => score), 0);

  if (maxScore === 0) {
    return [];
  }

  return scoredUsers
    .filter(({ score }) => score === maxScore)
    .map(({ user }) => user)
    .sort((left, right) =>
      left.email.localeCompare(right.email, undefined, { sensitivity: "base" }),
    );
};
